import type { SegmentPath, SegmentSpec, XzPoint } from "../types";

const EPS = 1e-9;

function copyPoint(p: XzPoint): XzPoint {
  return { x: p.x, z: p.z };
}

export function createLinePath(start: XzPoint, end: XzPoint): SegmentPath {
  return { start: copyPoint(start), end: copyPoint(end) };
}

export function createQuadraticPath(
  start: XzPoint,
  control: XzPoint,
  end: XzPoint,
): SegmentPath {
  return {
    start: copyPoint(start),
    control: copyPoint(control),
    end: copyPoint(end),
  };
}

export function clonePath(path: SegmentPath): SegmentPath {
  return {
    start: copyPoint(path.start),
    end: copyPoint(path.end),
    ...(path.control ? { control: copyPoint(path.control) } : {}),
  };
}

export function isCurvedPath(path: SegmentPath): boolean {
  return path.control !== undefined;
}

export function pathStart(path: SegmentPath): XzPoint {
  return copyPoint(path.start);
}

export function pathEnd(path: SegmentPath): XzPoint {
  return copyPoint(path.end);
}

export function pathPointAt(path: SegmentPath, t: number): XzPoint {
  const tc = Math.max(0, Math.min(1, t));
  return pathPointAtUnclamped(path, tc);
}

/** Polynomial uzantı/kesişim hesapları için t'yi [0,1]'e sıkıştırmaz. */
export function pathPointAtUnclamped(path: SegmentPath, tc: number): XzPoint {
  if (path.control) {
    const mt = 1 - tc;
    return {
      x: mt * mt * path.start.x + 2 * mt * tc * path.control.x + tc * tc * path.end.x,
      z: mt * mt * path.start.z + 2 * mt * tc * path.control.z + tc * tc * path.end.z,
    };
  }
  return {
    x: path.start.x + (path.end.x - path.start.x) * tc,
    z: path.start.z + (path.end.z - path.start.z) * tc,
  };
}

export function pathDerivativeAt(path: SegmentPath, t: number): XzPoint {
  if (!path.control) {
    return { x: path.end.x - path.start.x, z: path.end.z - path.start.z };
  }
  const tc = Math.max(0, Math.min(1, t));
  return {
    x: 2 * (1 - tc) * (path.control.x - path.start.x) + 2 * tc * (path.end.x - path.control.x),
    z: 2 * (1 - tc) * (path.control.z - path.start.z) + 2 * tc * (path.end.z - path.control.z),
  };
}

export function pathTangentAt(path: SegmentPath, t: number): XzPoint {
  const { x: dx, z: dz } = pathDerivativeAt(path, t);
  const len = Math.hypot(dx, dz);
  if (len < EPS) return { x: 1, z: 0 };
  return { x: dx / len, z: dz / len };
}

export function pathNormalAt(path: SegmentPath, t: number): XzPoint {
  const tangent = pathTangentAt(path, t);
  return { x: tangent.z, z: -tangent.x };
}

/** Line/quadratic path'in [startT,endT] analitik alt path'i. */
export function slicePath(
  path: SegmentPath,
  startT: number,
  endT: number,
): SegmentPath {
  const t0 = Math.max(0, Math.min(1, Math.min(startT, endT)));
  const t1 = Math.max(0, Math.min(1, Math.max(startT, endT)));
  const start = pathPointAt(path, t0);
  const end = pathPointAt(path, t1);
  if (!path.control) return { start, end };
  const derivative = pathDerivativeAt(path, t0);
  const scale = (t1 - t0) * 0.5;
  return {
    start,
    control: {
      x: start.x + derivative.x * scale,
      z: start.z + derivative.z * scale,
    },
    end,
  };
}

export interface PathProjection {
  point: XzPoint;
  t: number;
  distance: number;
  tangent: XzPoint;
  normal: XzPoint;
}

export interface PathSample {
  t: number;
  point: XzPoint;
}

export interface FlattenPathOptions {
  maxError?: number;
  maxDepth?: number;
}

function pointLineDistance(p: XzPoint, a: XzPoint, b: XzPoint): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz;
  if (lenSq < EPS) return Math.hypot(p.x - a.x, p.z - a.z);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq),
  );
  return Math.hypot(p.x - (a.x + dx * t), p.z - (a.z + dz * t));
}

/** Quadratic path'i hata toleransına göre line örneklerine ayırır. */
export function flattenPathAdaptive(
  path: SegmentPath,
  options: FlattenPathOptions = {},
): PathSample[] {
  const maxError = options.maxError ?? 0.005;
  const maxDepth = options.maxDepth ?? 10;
  const first = pathPointAt(path, 0);
  const samples: PathSample[] = [{ t: 0, point: first }];

  const visit = (
    t0: number,
    p0: XzPoint,
    t1: number,
    p1: XzPoint,
    depth: number,
  ): void => {
    const tm = (t0 + t1) * 0.5;
    const pm = pathPointAt(path, tm);
    if (depth < maxDepth && pointLineDistance(pm, p0, p1) > maxError) {
      visit(t0, p0, tm, pm, depth + 1);
      visit(tm, pm, t1, p1, depth + 1);
      return;
    }
    samples.push({ t: t1, point: p1 });
  };

  visit(0, first, 1, pathPointAt(path, 1), 0);
  return samples;
}

/** Line'da kapalı form; quadratic'te analitik mesafe türevini Newton ile çözer. */
export function projectPointOnPath(path: SegmentPath, target: XzPoint): PathProjection {
  const candidates = new Set<number>([0, 1]);
  if (!path.control) {
    const d = pathDerivativeAt(path, 0);
    const lenSq = d.x * d.x + d.z * d.z;
    if (lenSq > EPS) {
      candidates.add(Math.max(0, Math.min(1,
        ((target.x - path.start.x) * d.x + (target.z - path.start.z) * d.z) / lenSq,
      )));
    }
  } else {
    const second = {
      x: 2 * (path.end.x - 2 * path.control.x + path.start.x),
      z: 2 * (path.end.z - 2 * path.control.z + path.start.z),
    };
    for (let seed = 0; seed <= 8; seed++) {
      let t = seed / 8;
      for (let i = 0; i < 12; i++) {
        const p = pathPointAt(path, t);
        const d = pathDerivativeAt(path, t);
        const rx = p.x - target.x;
        const rz = p.z - target.z;
        const f = rx * d.x + rz * d.z;
        const df = d.x * d.x + d.z * d.z + rx * second.x + rz * second.z;
        if (Math.abs(df) < EPS) break;
        const next = Math.max(0, Math.min(1, t - f / df));
        if (Math.abs(next - t) < 1e-10) break;
        t = next;
      }
      candidates.add(t);
    }
  }

  let bestT = 0;
  let bestPoint = pathPointAt(path, 0);
  let bestDistance = Math.hypot(bestPoint.x - target.x, bestPoint.z - target.z);
  for (const t of candidates) {
    const point = pathPointAt(path, t);
    const distance = Math.hypot(point.x - target.x, point.z - target.z);
    if (distance < bestDistance) {
      bestT = t;
      bestPoint = point;
      bestDistance = distance;
    }
  }
  return {
    point: bestPoint,
    t: bestT,
    distance: bestDistance,
    tangent: pathTangentAt(path, bestT),
    normal: pathNormalAt(path, bestT),
  };
}

export function pathEndpoint(
  path: SegmentPath,
  role: "start" | "end",
): XzPoint {
  return role === "start" ? pathStart(path) : pathEnd(path);
}

export function segmentOriginLine(spec: SegmentSpec): {
  start: XzPoint;
  end: XzPoint;
} {
  return { start: pathStart(spec.path), end: pathEnd(spec.path) };
}

export function setPathEndpoint(
  spec: SegmentSpec,
  role: "start" | "end",
  point: XzPoint,
): void {
  if (role === "start") {
    spec.path.start = copyPoint(point);
  } else {
    spec.path.end = copyPoint(point);
  }
}

export function pathSpecEqual(
  a: SegmentPath,
  b: SegmentPath,
): boolean {
  return a.start.x === b.start.x && a.start.z === b.start.z &&
    a.end.x === b.end.x && a.end.z === b.end.z &&
    a.control?.x === b.control?.x && a.control?.z === b.control?.z;
}
